import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';

const SESSION_WARNING_MINUTES = 5;
const SESSION_TIMEOUT_MINUTES = 30;

export default function SessionWarning() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(null);

  const sessionTimeout = (user?.session_timeout_minutes || SESSION_TIMEOUT_MINUTES) * 60 * 1000;
  const warningTime = (user?.session_warning_minutes || SESSION_WARNING_MINUTES) * 60 * 1000;

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
    sessionStorage.removeItem('pin_verified');
    navigate('/');
  }, [logout, navigate]);

  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    const warningSeconds = Math.floor(warningTime / 1000);
    setRemainingTime(warningSeconds);
    
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    countdownRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningTime, handleLogout]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Main effect for session management
  useEffect(() => {
    if (!token || !user) return;

    // Initialize
    lastActivityRef.current = Date.now();

    // Check session periodically
    const checkSession = () => {
      const now = Date.now();
      const timeSinceActivity = now - (lastActivityRef.current || now);
      const timeUntilExpiry = sessionTimeout - timeSinceActivity;

      if (timeUntilExpiry <= 0) {
        handleLogout();
      } else if (timeUntilExpiry <= warningTime && !showWarning) {
        startWarningCountdown();
      }
    };

    // Check every 10 seconds
    timerRef.current = setInterval(checkSession, 10000);

    // Activity listeners
    const handleActivity = () => {
      if (!showWarning) {
        lastActivityRef.current = Date.now();
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [token, user, sessionTimeout, warningTime, showWarning, handleLogout, startWarningCountdown]);

  const handleExtendSession = () => {
    resetActivity();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showWarning || !token) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-testid="session-warning-modal">
      <div className="bg-card border rounded-lg shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-full bg-amber-500/20">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Session sur le point d&apos;expirer</h3>
            <p className="text-sm text-muted-foreground">
              Votre session expirera dans
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-center my-6">
          <div className="flex items-center gap-2 px-6 py-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <Clock className="w-6 h-6 text-amber-500" />
            <span className="text-3xl font-bold text-amber-500" data-testid="session-countdown">
              {formatTime(remainingTime)}
            </span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground text-center mb-6">
          Pour des raisons de sécurité, vous serez automatiquement déconnecté après cette période d&apos;inactivité.
        </p>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={handleLogout}
            data-testid="session-logout-btn"
          >
            Se déconnecter
          </Button>
          <Button 
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            onClick={handleExtendSession}
            data-testid="session-extend-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Prolonger la session
          </Button>
        </div>
      </div>
    </div>
  );
}
